import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { EmptyState } from "@/components/EmptyState";
import { PaginationBar } from "@/components/PaginationBar";
import { RecordHealthPanel } from "@/components/RecordHealthPanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { getServiceTicketBlockers } from "@/data/unifiedBlockers";
import { getServiceTicketNextSteps } from "@/data/nextStepEngine";
import {
  SERVICE_TICKET_CATEGORIES,
  SERVICE_TICKET_PRIORITIES,
  SERVICE_TICKET_STATUSES,
  TICKET_PRIORITY_COLORS,
  TICKET_STATUS_COLORS,
  type ServiceTicket,
  type ServiceTicketCategory,
  type ServiceTicketPriority,
  type ServiceTicketStatus,
} from "@/data/serviceTicketTypes";
import { useResolveServiceJobOptions, useResolveServiceServerData } from "@/hooks/useResolveServiceServerPaging";
import { useCountedServerPagination } from "@/hooks/useServerPagination";
import type { ResolveServiceTab } from "@/services/resolve-service-page.service";
import { ServiceTicketService } from "@/services/service-ticket.service";
import { getCurrentRepName } from "@/lib/repIdentity";
import { ArrowRight, AlertTriangle, CheckCircle, Clock, Plus, Search, Wrench } from "lucide-react";
import { toast } from "sonner";

const activeStatuses: ServiceTicketStatus[] = ["Open", "In Progress", "Awaiting Parts", "Scheduled"];
const archivedStatuses: ServiceTicketStatus[] = ["Resolved", "Closed"];

export default function ResolveServiceTicketsServerPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ServiceTicketStatus | "all">("all");
  const [tab, setTab] = useState<ResolveServiceTab>("active");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<ServiceTicket | null>(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveStatus, setResolveStatus] = useState<"Resolved" | "Closed">("Resolved");
  const [resolutionNotes, setResolutionNotes] = useState("");

  const [newJobId, setNewJobId] = useState("");
  const [newCategory, setNewCategory] = useState<ServiceTicketCategory>("Callback");
  const [newPriority, setNewPriority] = useState<ServiceTicketPriority>("Medium");
  const [newSubject, setNewSubject] = useState("");
  const [newDescription, setNewDescription] = useState("");

  const pagination = useCountedServerPagination(25);
  const { reset, setTotalItems } = pagination;
  const serverData = useResolveServiceServerData({
    enabled: true,
    page: pagination.page,
    pageSize: pagination.pageSize,
    search,
    status: statusFilter,
    tab,
  });
  const jobOptions = useResolveServiceJobOptions({
    enabled: createOpen,
    limit: 75,
    refreshToken: serverData.refreshToken,
  });

  useEffect(() => {
    reset();
  }, [reset, search, statusFilter, tab]);

  useEffect(() => {
    setTotalItems(serverData.count);
  }, [serverData.count, setTotalItems]);

  const visibleStatuses = tab === "active" ? activeStatuses : archivedStatuses;
  const summary = serverData.summary;
  const currentUser = getCurrentRepName();
  const selectedJob = useMemo(
    () => jobOptions.data.find((job) => job.jobLabel === newJobId || job.jobId === newJobId) ?? null,
    [jobOptions.data, newJobId],
  );

  const resetCreateForm = () => {
    setNewJobId("");
    setNewCategory("Callback");
    setNewPriority("Medium");
    setNewSubject("");
    setNewDescription("");
  };

  const handleCreate = async () => {
    if (!newSubject.trim() || !newJobId) {
      toast.error("Subject and Job are required");
      return;
    }
    const job = selectedJob;
    if (!job) {
      toast.error("Selected job not found");
      return;
    }

    try {
      const nextId = await ServiceTicketService.getNextTicketId();
      const ticket: ServiceTicket = {
        id: nextId,
        jobId: job.jobLabel || job.jobId,
        customerId: "",
        customerName: job.customerName || "",
        customerPhone: "",
        customerEmail: "",
        address: "",
        category: newCategory,
        priority: newPriority,
        status: "Open",
        subject: newSubject.trim(),
        description: newDescription.trim(),
        assignedTo: "",
        createdBy: currentUser,
        createdAt: new Date().toISOString().split("T")[0],
        updatedAt: new Date().toISOString().split("T")[0],
        activities: [{ id: `act-${Date.now()}`, action: "Ticket created", user: currentUser, createdAt: new Date().toISOString() }],
        notes: "",
      };

      const result = await ServiceTicketService.create(ticket);
      if (result.data) {
        setCreateOpen(false);
        resetCreateForm();
        serverData.refresh();
        toast.success(`Service ticket ${result.data.id} created`);
      } else {
        toast.error("Failed to create ticket");
      }
    } catch (error) {
      toast.error("Error creating ticket");
      console.error(error);
    }
  };

  const handleStatusChange = async (newStatus: ServiceTicketStatus) => {
    if (!selectedTicket) return;
    try {
      const result = await ServiceTicketService.update(selectedTicket.id, { status: newStatus });
      if (result.data) {
        setSelectedTicket(result.data);
        await ServiceTicketService.addActivity(selectedTicket.id, {
          id: `act-${Date.now()}`,
          action: `Status changed to ${newStatus}`,
          user: currentUser,
          createdAt: new Date().toISOString(),
        });
        serverData.refresh();
        toast.success(`Status updated to ${newStatus}`);
      }
    } catch (error) {
      toast.error("Failed to update status");
      console.error(error);
    }
  };

  const handleResolveClose = async () => {
    if (!selectedTicket) return;
    try {
      const payload: Partial<ServiceTicket> = {
        status: resolveStatus,
        resolution: resolutionNotes,
        resolvedAt: new Date().toISOString().split("T")[0],
        resolvedBy: currentUser,
      };
      const result = await ServiceTicketService.update(selectedTicket.id, payload);
      if (result.data) {
        setSelectedTicket(result.data);
        await ServiceTicketService.addActivity(selectedTicket.id, {
          id: `act-${Date.now()}`,
          action: `Ticket ${resolveStatus.toLowerCase()}`,
          user: currentUser,
          createdAt: new Date().toISOString(),
        });
        setResolveOpen(false);
        setResolutionNotes("");
        serverData.refresh();
        toast.success(`Ticket ${resolveStatus.toLowerCase()}`);
      }
    } catch (error) {
      toast.error("Failed to resolve/close ticket");
      console.error(error);
    }
  };

  const tickets = serverData.tickets;
  const isLoading = serverData.loading;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Service Tickets</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {summary?.active ?? 0} active · {summary?.archived ?? 0} resolved
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" /> New Ticket
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Open", value: summary?.open ?? 0, icon: Clock, color: "text-info" },
          { label: "In Progress", value: summary?.inProgress ?? 0, icon: Wrench, color: "text-warning" },
          { label: "Urgent", value: summary?.urgentActive ?? 0, icon: AlertTriangle, color: "text-destructive" },
          { label: "Resolved", value: summary?.archived ?? 0, icon: CheckCircle, color: "text-success" },
        ].map((kpi) => (
          <Card key={kpi.label} className="border shadow-none">
            <CardContent className="p-3 text-center">
              <kpi.icon className={`h-4 w-4 mx-auto mb-1 ${kpi.color}`} />
              <p className="text-xl font-bold">{kpi.value}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-medium">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={(value) => { setTab(value as ResolveServiceTab); setStatusFilter("all"); }}>
        <TabsList>
          <TabsTrigger value="active" className="text-xs gap-1.5">
            <Wrench className="h-3.5 w-3.5" /> Active ({summary?.active ?? 0})
          </TabsTrigger>
          <TabsTrigger value="archived" className="text-xs gap-1.5">
            <CheckCircle className="h-3.5 w-3.5" /> Resolved ({summary?.archived ?? 0})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input placeholder="Search tickets..." value={search} onChange={(event) => setSearch(event.target.value)} className="pl-8 h-8 text-xs" />
        </div>
        <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as ServiceTicketStatus | "all")}>
          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All {tab === "active" ? "Active" : "Resolved"}</SelectItem>
            {visibleStatuses.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="shadow-none">
        <CardContent className="p-0">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">ID</th>
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Subject</th>
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Customer</th>
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Category</th>
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Priority</th>
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Status</th>
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Job</th>
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Created</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => (
                <tr key={ticket.id} className="border-b last:border-0 hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => setSelectedTicket(ticket)}>
                  <td className="px-4 py-3 font-medium text-primary">{ticket.id}</td>
                  <td className="px-4 py-3 font-medium max-w-[200px] truncate">{ticket.subject}</td>
                  <td className="px-4 py-3">
                    <Link to={`/customers/${encodeURIComponent(ticket.customerName)}`} className="hover:underline text-primary" onClick={(event) => event.stopPropagation()}>{ticket.customerName}</Link>
                  </td>
                  <td className="px-4 py-3">{ticket.category}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-md border text-[10px] font-semibold ${TICKET_PRIORITY_COLORS[ticket.priority]}`}>{ticket.priority}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-md border text-[10px] font-semibold ${TICKET_STATUS_COLORS[ticket.status]}`}>{ticket.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/jobs/${ticket.jobId}`} className="text-primary hover:underline inline-flex items-center gap-1" onClick={(event) => event.stopPropagation()}>{ticket.jobId} <ArrowRight className="h-3 w-3" /></Link>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{ticket.createdAt || "—"}</td>
                </tr>
              ))}
              {!isLoading && tickets.length === 0 && (
                <tr><td colSpan={8} className="p-0">
                  <EmptyState
                    icon={Wrench}
                    title="No service tickets"
                    description="Tickets appear here when post-install issues are reported. You can also create one manually."
                    actionLabel="Create Ticket"
                    onAction={() => setCreateOpen(true)}
                  />
                </td></tr>
              )}
              {isLoading && tickets.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Loading service tickets...</td></tr>
              )}
            </tbody>
          </table>
          <PaginationBar {...pagination} />
        </CardContent>
      </Card>

      {selectedTicket && (
        <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wrench className="h-4 w-4" /> {selectedTicket.id} - {selectedTicket.subject}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{selectedTicket.customerName}</span></div>
                <div><span className="text-muted-foreground">Job:</span> <Link to={`/jobs/${selectedTicket.jobId}`} className="text-primary hover:underline">{selectedTicket.jobId}</Link></div>
                <div><span className="text-muted-foreground">Category:</span> {selectedTicket.category}</div>
                <div><span className="text-muted-foreground">Assigned:</span> {selectedTicket.assignedTo || "Unassigned"}</div>
                <div><span className="text-muted-foreground">Priority:</span> <span className={`inline-flex px-2 py-0.5 rounded-md border text-[10px] font-semibold ${TICKET_PRIORITY_COLORS[selectedTicket.priority]}`}>{selectedTicket.priority}</span></div>
                <div><span className="text-muted-foreground">Status:</span>
                  <Select value={selectedTicket.status} onValueChange={(value) => handleStatusChange(value as ServiceTicketStatus)}>
                    <SelectTrigger className="inline-flex h-7 text-xs w-32 ml-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SERVICE_TICKET_STATUSES.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {selectedTicket.description && <p className="text-xs text-muted-foreground">{selectedTicket.description}</p>}
              <RecordHealthPanel
                title="Ticket Health"
                blockers={getServiceTicketBlockers(selectedTicket)}
                nextSteps={getServiceTicketNextSteps(selectedTicket)}
                completeness={[
                  { label: "Assigned", done: !!selectedTicket.assignedTo },
                  { label: "Description", done: !!selectedTicket.description },
                  { label: "Scheduled", done: !!selectedTicket.scheduledDate || !["Scheduled", "In Progress"].includes(selectedTicket.status) },
                  { label: "Resolution", done: !!selectedTicket.resolution },
                ]}
                compact
              />
              <div className="flex gap-2 pt-2">
                {!["Resolved", "Closed"].includes(selectedTicket.status) && (
                  <Button variant="outline" size="sm" onClick={() => setResolveOpen(true)}>
                    Resolve / Close
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle className="h-4 w-4" /> Resolve or Close Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Final Status</Label>
              <Select value={resolveStatus} onValueChange={(value) => setResolveStatus(value as "Resolved" | "Closed")}>
                <SelectTrigger className="mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Resolved">Resolved</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Resolution Notes *</Label>
              <Textarea value={resolutionNotes} onChange={(event) => setResolutionNotes(event.target.value)} placeholder="Describe how the issue was resolved..." className="mt-1" rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveOpen(false)}>Cancel</Button>
            <Button onClick={handleResolveClose} disabled={!resolutionNotes.trim()}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wrench className="h-4 w-4" /> New Service Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Linked Job *</Label>
              <Select value={newJobId} onValueChange={setNewJobId}>
                <SelectTrigger className="mt-1 text-xs"><SelectValue placeholder={jobOptions.loading ? "Loading jobs..." : "Select job..."} /></SelectTrigger>
                <SelectContent>
                  {jobOptions.data.map((job) => (
                    <SelectItem key={job.jobId} value={job.jobLabel}>{job.jobLabel} - {job.customerName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={newCategory} onValueChange={(value) => setNewCategory(value as ServiceTicketCategory)}>
                  <SelectTrigger className="mt-1 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICE_TICKET_CATEGORIES.map((category) => <SelectItem key={category} value={category}>{category}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <Select value={newPriority} onValueChange={(value) => setNewPriority(value as ServiceTicketPriority)}>
                  <SelectTrigger className="mt-1 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICE_TICKET_PRIORITIES.map((priority) => <SelectItem key={priority} value={priority}>{priority}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Subject *</Label>
              <Input value={newSubject} onChange={(event) => setNewSubject(event.target.value)} placeholder="Brief description" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={newDescription} onChange={(event) => setNewDescription(event.target.value)} placeholder="Detailed description..." className="mt-1" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!newSubject.trim() || !newJobId}>Create Ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
